import React, { useEffect, useMemo, useState, useRef } from 'react';
import { MoreHorizontal, AlertCircle, CheckCircle2, Clock, RefreshCw, Eye, Pause, ExternalLink } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
export interface DialingList {
  id: string;
  name: string;
  leadCount: number;
  inflow: number;
  outflow: number;
  sfCount: number;
  five9Count: number;
  health: 'healthy' | 'warning' | 'critical';
  lastUpdate: string;
  trendData: {
    value: number;
  }[];
}
function makeTrend(seed: number) {
  let s = seed;
  const rand = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  return Array.from({
    length: 14
  }).map((_, i) => ({
    value: 5000 + rand() * 2000 + i * 100
  }));
}
const INITIAL_LISTS: DialingList[] = [{
  id: '1',
  name: 'Dominate',
  leadCount: 45210,
  inflow: 3200,
  outflow: 2800,
  sfCount: 45210,
  five9Count: 45210,
  health: 'healthy',
  lastUpdate: '2 mins ago',
  trendData: makeTrend(101)
}, {
  id: '2',
  name: 'VA Cash Out',
  leadCount: 18450,
  inflow: 1100,
  outflow: 1400,
  sfCount: 18450,
  five9Count: 18448,
  health: 'warning',
  lastUpdate: '5 mins ago',
  trendData: makeTrend(202)
}, {
  id: '3',
  name: 'Rate Watch',
  leadCount: 32100,
  inflow: 4500,
  outflow: 1200,
  sfCount: 32100,
  five9Count: 31950,
  health: 'critical',
  lastUpdate: '12 mins ago',
  trendData: makeTrend(303)
}, {
  id: '4',
  name: 'Shark Tank',
  leadCount: 8900,
  inflow: 400,
  outflow: 600,
  sfCount: 8900,
  five9Count: 8900,
  health: 'healthy',
  lastUpdate: '1 min ago',
  trendData: makeTrend(404)
}, {
  id: '5',
  name: 'Low FICO',
  leadCount: 12400,
  inflow: 800,
  outflow: 750,
  sfCount: 12400,
  five9Count: 12400,
  health: 'healthy',
  lastUpdate: '8 mins ago',
  trendData: makeTrend(505)
}];
const HEALTH_FILTERS = ['All', 'Healthy', 'Warning', 'Critical'] as const;
type HealthFilter = (typeof HEALTH_FILTERS)[number];
interface DialingListGridProps {
  onSelectList: (list: DialingList) => void;
  selectedListId?: string;
}
export function DialingListGrid({
  onSelectList,
  selectedListId
}: DialingListGridProps) {
  const [filter, setFilter] = useState<HealthFilter>('All');
  const [lists, setLists] = useState<DialingList[]>(INITIAL_LISTS);
  const [pausedIds, setPausedIds] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  const filteredLists = useMemo(() => {
    if (filter === 'All') return lists;
    return lists.filter(l => l.health === filter.toLowerCase() as DialingList['health']);
  }, [filter, lists]);
  const counts = useMemo(() => ({
    All: lists.length,
    Healthy: lists.filter(l => l.health === 'healthy').length,
    Warning: lists.filter(l => l.health === 'warning').length,
    Critical: lists.filter(l => l.health === 'critical').length
  }), [lists]);
  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    setTimeout(() => {
      // Simulate slight count changes
      setLists(prev => prev.map(l => ({
        ...l,
        leadCount: l.leadCount + Math.floor((Math.random() - 0.3) * 50),
        lastUpdate: 'just now'
      })));
      setRefreshing(false);
    }, 900);
  };
  const togglePause = (id: string) => {
    setPausedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
    setOpenMenu(null);
  };
  return <div className="mb-8" ref={containerRef} data-id="element-143">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4" data-id="element-144">
        <h2 className="text-lg font-semibold text-white" data-id="element-145">
          Primary Routing Lists
        </h2>
        <div className="flex items-center gap-3 flex-wrap" data-id="element-146">
          <div className="flex bg-slate-950 border border-slate-800 rounded-lg p-1" data-id="element-147">
            {HEALTH_FILTERS.map(f => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${filter === f ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-148">
                {f === 'Healthy' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-149"></span>}
                {f === 'Warning' && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" data-id="element-150"></span>}
                {f === 'Critical' && <span className="w-1.5 h-1.5 rounded-full bg-rose-400" data-id="element-151"></span>}
                {f}
                <span className="text-[10px] text-slate-500 tabular-nums" data-id="element-152">
                  {counts[f]}
                </span>
              </button>)}
          </div>
          <button onClick={handleRefresh} disabled={refreshing} className="text-sm text-indigo-400 hover:text-indigo-300 disabled:opacity-60 font-medium flex items-center gap-1.5 px-2 py-1" data-id="element-153">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} data-id="element-154" />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {filteredLists.length === 0 ? <div className="bg-slate-900/50 border border-dashed border-slate-800 rounded-xl p-12 text-center" data-id="element-155">
          <p className="text-slate-400 text-sm" data-id="element-156">No lists match this filter.</p>
        </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4" data-id="element-157">
          {filteredLists.map(list => {
        const isSelected = selectedListId === list.id;
        const isPaused = pausedIds.has(list.id);
        const variance = Math.abs(list.sfCount - list.five9Count);
        const isMenuOpen = openMenu === list.id;
        return <div key={list.id} className={`relative bg-slate-900/80 border rounded-xl transition-all duration-200 group
                  ${isSelected ? 'border-indigo-500 ring-1 ring-indigo-500/50 shadow-[0_0_20px_rgba(99,102,241,0.1)]' : 'border-slate-800/60 hover:border-slate-700'}
                  ${isPaused ? 'opacity-70' : ''}
                `} data-id="element-158">
                <button onClick={() => onSelectList(list)} aria-pressed={isSelected} className="text-left w-full p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 rounded-xl" data-id="element-159">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4" data-id="element-160">
                    <div className="min-w-0 pr-8" data-id="element-161">
                      <div className="flex items-center gap-2" data-id="element-162">
                        <h3 className="font-semibold text-slate-200 group-hover:text-white transition-colors truncate" data-id="element-163">
                          {list.name}
                        </h3>
                        {isPaused && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded" data-id="element-164">
                            Paused
                          </span>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500" data-id="element-165">
                        <Clock className="w-3 h-3" data-id="element-166" />
                        <span data-id="element-167">Updated {list.lastUpdate}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4" data-id="element-168">
                    <div className="text-3xl font-bold text-white tabular-nums tracking-tight" data-id="element-169">
                      {list.leadCount.toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-400 mt-1" data-id="element-170">
                      Active Leads
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4" data-id="element-171">
                    <div className="bg-slate-950/50 rounded-lg p-2 border border-slate-800/50" data-id="element-172">
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-173">
                        Inflow
                      </div>
                      <div className="text-sm font-medium text-emerald-400 tabular-nums" data-id="element-174">
                        +{list.inflow.toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-slate-950/50 rounded-lg p-2 border border-slate-800/50" data-id="element-175">
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-176">
                        Outflow
                      </div>
                      <div className="text-sm font-medium text-rose-400 tabular-nums" data-id="element-177">
                        -{list.outflow.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="h-12 -mx-2 mb-4 opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-178">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-179">
                      <AreaChart data={list.trendData} data-id="element-180">
                        <defs data-id="element-181">
                          <linearGradient id={`color-${list.id}`} x1="0" y1="0" x2="0" y2="1" data-id="element-182">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} data-id="element-183" />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} data-id="element-184" />
                          </linearGradient>
                        </defs>
                        <Area type="monotone" dataKey="value" stroke="#6366f1" fillOpacity={1} fill={`url(#color-${list.id})`} strokeWidth={2} isAnimationActive={false} data-id="element-185" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="pt-3 border-t border-slate-800/60 flex items-center justify-between" data-id="element-186">
                    <div className="flex items-center gap-2" data-id="element-187">
                      {list.health === 'healthy' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-188" /> : list.health === 'warning' ? <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-189" /> : <AlertCircle className="w-4 h-4 text-rose-500" data-id="element-190" />}
                      <span className="text-xs font-medium text-slate-300" data-id="element-191">
                        Sync Status
                      </span>
                    </div>
                    {variance > 0 ? <span className={`text-xs font-bold tabular-nums ${list.health === 'critical' ? 'text-rose-400' : 'text-amber-400'}`} data-id="element-192">
                        {variance} mismatch
                      </span> : <span className="text-xs font-medium text-emerald-400" data-id="element-193">
                        Synced
                      </span>}
                  </div>
                </button>

                {/* Menu button (overlay, outside the main button) */}
                <button onClick={e => {
            e.stopPropagation();
            setOpenMenu(cur => cur === list.id ? null : list.id);
          }} aria-label="List actions" className="absolute top-4 right-4 p-1 text-slate-500 hover:text-slate-200 hover:bg-slate-800 rounded transition-colors z-10" data-id="element-194">
                  <MoreHorizontal className="w-4 h-4" data-id="element-195" />
                </button>

                {isMenuOpen && <div className="absolute top-12 right-3 w-48 bg-slate-900 border border-slate-800 rounded-lg shadow-2xl overflow-hidden z-20 py-1" data-id="element-196">
                    <button onClick={e => {
              e.stopPropagation();
              onSelectList(list);
              setOpenMenu(null);
            }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors" data-id="element-197">
                      <Eye className="w-4 h-4" data-id="element-198" />
                      View Details
                    </button>
                    <button onClick={e => {
              e.stopPropagation();
              setOpenMenu(null);
            }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors" data-id="element-199">
                      <ExternalLink className="w-4 h-4" data-id="element-200" />
                      Open in Five9
                    </button>
                    <div className="border-t border-slate-800 my-1" data-id="element-201"></div>
                    <button onClick={e => {
              e.stopPropagation();
              togglePause(list.id);
            }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-amber-400 hover:bg-amber-500/10 transition-colors" data-id="element-202">
                      <Pause className="w-4 h-4" data-id="element-203" />
                      {isPaused ? 'Resume Routing' : 'Pause Routing'}
                    </button>
                  </div>}
              </div>;
      })}
        </div>}
    </div>;
}